/**
 * The side panel (canon FV4-13, FV6-03 to FV6-07): the movement's newest recording, or the older
 * one a double click on the chart put there, or the request for one. Folded, it is a 64px rail
 * with the newest recording's miniature and date.
 *
 * ONE aside in both states, with one arrow and one body box: folding keeps the arrow's focus, and
 * the body's hand-over does not replay on a fold, an unfold or a double click.
 *
 * The recording plays in the card, in `features/clip-player`'s small player; folding the panel
 * unmounts it, which stops the film.
 */

import { useId, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { ClipMiniPlayer } from '~/features/clip-player';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import { CheckGlyph } from '~/shared/ui/FormGlyphs';
import iconButton from '~/shared/ui/iconButton.module.css';
import inkButton from '~/shared/ui/inkButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import photo from '~/shared/ui/photoBox.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import { CameraGlyph, ChevronGlyph, FilmGlyph, PlayGlyph, PushPinGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';

import { useRefreshTrainedExercises } from '../lib/useForma';
import type { FormaDockPanel } from '../lib/useFormaDockPanel';
import type { RecordingRequest } from '../lib/useRecordingRequest';
import type { FormaDock as FormaDockView, FormaRail } from '../model/formaView';

import styles from './Forma.module.css';

const ANGLES = ['side', 'front45', 'front', 'back'] as const;
type Angle = (typeof ANGLES)[number];

interface FormaDockProps {
  readonly clientId: number;
  readonly dock: FormaDockView | null;
  /** The movement's name, which the panel's film is announced by. */
  readonly exerciseName: string;
  /** The full player is open over the tab, so the panel's film holds still. */
  readonly playerOpen: boolean;
  readonly rail: FormaRail | null;
  readonly panel: FormaDockPanel;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly request: RecordingRequest;
  /** Open the full player on a recording, at a second when the panel's film was left there. */
  readonly onOpenClip: (clipId: number, startAt?: number | null) => void;
}

export function FormaDock({
  clientId,
  dock,
  exerciseName,
  playerOpen,
  rail,
  panel,
  gate,
  failed,
  onRetry,
  request,
  onOpenClip,
}: FormaDockProps) {
  const { t } = useTranslation();
  const arrow = useRef<HTMLButtonElement>(null);
  const hintId = useId();
  const { open } = panel;
  const arrowLabel = t(open ? 'web:forma.panel.collapse' : 'web:forma.panel.expand');
  // A miniature that unfolds the panel leaves with the rail; the arrow stands and takes the focus.
  const expandFromRail = (retry: boolean) => {
    panel.expand();
    if (retry) onRetry();
    arrow.current?.focus();
  };

  return (
    <aside
      aria-busy={gate.waiting ? 'true' : undefined}
      aria-label={t(open ? 'web:forma.dock.label' : 'web:forma.panel.rail')}
      className={open ? styles.dock : styles.rail}
    >
      <div className={open ? styles.dockHead : undefined}>
        {open ? (
          <>
            <button
              aria-describedby={hintId}
              aria-pressed={panel.pinned}
              className={`${String(styles.pressChip)} ${String(styles.pinButton)} ${panel.pinned ? String(styles.pressChipOn) : ''}`}
              onClick={panel.togglePin}
              title={t('web:forma.panel.pinHint')}
              type="button"
            >
              <PushPinGlyph className={styles.pinGlyph} />
              {t('web:forma.panel.pin')}
            </button>
            <span className={hidden.srOnly} id={hintId}>
              {t('web:forma.panel.pinHint')}
            </span>
          </>
        ) : null}
        <button
          aria-label={arrowLabel}
          className={`${String(iconButton.iconButton)} ${String(styles.panelArrow)}`}
          onClick={open ? panel.collapse : panel.expand}
          ref={arrow}
          title={arrowLabel}
          type="button"
        >
          <ChevronGlyph className={styles.panelArrowGlyph} direction={open ? 'right' : 'left'} />
        </button>
      </div>
      <div className={`${open ? String(styles.dockBody) : String(styles.railBody)} ${gate.reveal}`}>
        {open ? (
          <OpenBody
            clientId={clientId}
            dock={dock}
            exerciseName={exerciseName}
            failed={failed}
            gate={gate}
            onOpenClip={onOpenClip}
            onRetry={onRetry}
            onShowNewest={panel.showNewest}
            playerOpen={playerOpen}
            request={request}
          />
        ) : (
          <RailBody failed={failed} gate={gate} onExpand={expandFromRail} onOpenClip={onOpenClip} rail={rail} />
        )}
      </div>
    </aside>
  );
}

function OpenBody({
  clientId,
  dock,
  exerciseName,
  playerOpen,
  gate,
  failed,
  onRetry,
  request,
  onOpenClip,
  onShowNewest,
}: {
  readonly clientId: number;
  readonly dock: FormaDockView | null;
  readonly exerciseName: string;
  readonly playerOpen: boolean;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly request: RecordingRequest;
  readonly onOpenClip: (clipId: number, startAt?: number | null) => void;
  readonly onShowNewest: () => void;
}) {
  const { t } = useTranslation();
  const openButton = useRef<HTMLButtonElement>(null);

  if (gate.waiting) {
    return (
      <div className={`${String(styles.dockCard)} ${String(styles.dockWait)}`}>
        <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
        {gate.skeleton ? <i className={`${String(skel.bar)} ${String(styles.skelPoster)}`} /> : null}
      </div>
    );
  }

  if (failed) {
    return (
      <p className={`${String(styles.dockCard)} ${String(styles.listNotice)}`}>
        {t('web:forma.dock.failed')}{' '}
        <button className={inlineButton.inlineTextButton} onClick={onRetry} type="button">
          {t('web:clientDetail.states.retry')}
        </button>
      </p>
    );
  }

  if (dock === null) return <AskCard request={request} />;

  return (
    <>
      {dock.chosenDate === null ? null : (
        <p className={styles.dockWhich}>
          {t('web:forma.panel.chosen', { date: dock.chosenDate })}
          <button
            className={`${String(inlineButton.inlineTextButton)} ${String(styles.dockWhichLink)}`}
            onClick={() => {
              onShowNewest();
              openButton.current?.focus();
            }}
            type="button"
          >
            {t('web:forma.panel.newest')}
          </button>
        </p>
      )}
      <DockCard
        clientId={clientId}
        dock={dock}
        exerciseName={exerciseName}
        onOpenClip={onOpenClip}
        openRef={openButton}
        playerOpen={playerOpen}
      />
    </>
  );
}

function AskCard({ request }: { readonly request: RecordingRequest }) {
  const { t } = useTranslation();
  const [angle, setAngle] = useState<Angle>('side');
  const askLine = t('web:forma.dock.askLine', { hint: t(`web:forma.dock.hint.${angle}`) });
  return (
    <div className={`${String(styles.dockCard)} ${String(styles.askCard)}`}>
      <h3 className={styles.askTitle}>{t('web:forma.dock.empty')}</h3>
      <span className={styles.eyebrow}>{t('web:forma.dock.ask')}</span>
      <div aria-label={t('web:forma.dock.angles')} className={styles.angles} role="group">
        {ANGLES.map((option) => (
          <button
            aria-pressed={option === angle}
            className={`${String(styles.angle)} ${option === angle ? String(styles.angleOn) : ''}`}
            key={option}
            onClick={() => {
              setAngle(option);
            }}
            type="button"
          >
            {t(`web:forma.dock.angle.${option}`)}
          </button>
        ))}
      </div>
      <p className={styles.askLine}>{askLine}</p>
      {request.status === 'sent' ? (
        <span className={styles.askSent}>
          <CheckGlyph className={styles.askGlyph} />
          {t('web:forma.dock.sent')}
        </span>
      ) : (
        <button
          aria-busy={request.status === 'sending' ? 'true' : undefined}
          className={`${String(inkButton.ink)} ${String(styles.askButton)}`}
          onClick={() => {
            request.send(askLine);
          }}
          type="button"
        >
          <CameraGlyph className={styles.askGlyph} />
          {t('web:forma.dock.send')}
        </button>
      )}
      <p className={styles.askStatus} role="status">
        {request.status === 'noEmail'
          ? t('web:forma.dock.noEmail')
          : request.status === 'failed'
            ? t('web:forma.dock.sendFailed')
            : ''}
      </p>
    </div>
  );
}

function DockCard({
  clientId,
  dock,
  exerciseName,
  playerOpen,
  openRef,
  onOpenClip,
}: {
  readonly clientId: number;
  readonly dock: FormaDockView;
  readonly exerciseName: string;
  readonly playerOpen: boolean;
  readonly openRef: RefObject<HTMLButtonElement | null>;
  readonly onOpenClip: (clipId: number, startAt?: number | null) => void;
}) {
  const { t } = useTranslation();
  const refreshTrainedExercises = useRefreshTrainedExercises(clientId);
  return (
    <div className={styles.dockCard}>
      <ClipMiniPlayer
        clientId={clientId}
        clip={dock.clip}
        held={playerOpen}
        label={exerciseName}
        onActivity={refreshTrainedExercises}
        onOpen={(startAt) => {
          onOpenClip(dock.clipId, startAt);
        }}
        openRef={openRef}
      />
      <div className={styles.dockSetRow}>
        <span className={styles.dockKgReps}>{dock.kgReps ?? ''}</span>
        <span
          className={`${String(styles.sessionChip)} ${dock.chip.kind === 'outside' ? String(styles.sessionChipOff) : ''}`}
        >
          {dock.chip.label}
        </span>
      </div>
      <div className={styles.dockMeta}>
        <span className={styles.dockDate}>{dock.dateLabel}</span>
        <span className={styles.dockBy}>
          <span className={hidden.srOnly}>{t('web:forma.dock.by')}</span>
          <AvatarChip avatarUrl={dock.by.avatarUrl} initials={dock.by.initials} variant="clipSmall" />
          {dock.by.name}
        </span>
      </div>
      {dock.comments.length === 0 ? null : (
        <ol aria-label={t('web:forma.dock.comments')} className={styles.dockThread}>
          {dock.comments.map((comment) => (
            <li key={comment.id}>
              <div className={styles.dockComment}>
                <AvatarChip
                  avatarUrl={comment.person.avatarUrl}
                  initials={comment.person.initials}
                  variant="clip"
                />
                <span className={styles.dockCommentText}>
                  <span className={styles.dockCommentHead}>
                    <b>{comment.person.name}</b>
                    <span className={styles.dockCommentWhen}>{comment.when}</span>
                    {comment.stamp === null ? null : (
                      <span className={styles.dockCommentStamp}>{comment.stamp}</span>
                    )}
                  </span>
                  <span className={styles.dockCommentBody}>{comment.body}</span>
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * The folded panel's body: the newest recording's miniature (a click opens the player), its unread
 * dot and its date written upwards. With no recording its miniature asks for one; after a failed
 * read it says so, and unfolding reads again.
 */
function RailBody({
  rail,
  gate,
  failed,
  onOpenClip,
  onExpand,
}: {
  readonly rail: FormaRail | null;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly onOpenClip: (clipId: number) => void;
  readonly onExpand: (retry: boolean) => void;
}) {
  const { t } = useTranslation();
  if (gate.waiting) {
    return (
      <>
        <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
        {gate.skeleton ? <i className={`${String(skel.bar)} ${String(styles.skelRailThumb)}`} /> : null}
      </>
    );
  }
  if (failed || rail === null) {
    return (
      <>
        <button
          aria-label={failed ? t('web:forma.panel.failed') : t('web:forma.panel.noneAsk')}
          className={`${String(styles.railThumb)} ${String(styles.railThumbNone)}`}
          onClick={() => {
            onExpand(failed);
          }}
          type="button"
        >
          {failed ? (
            <WarningGlyph className={styles.railNoneGlyph} />
          ) : (
            <FilmGlyph className={styles.railNoneGlyph} />
          )}
        </button>
        {failed ? null : (
          <span aria-hidden="true" className={styles.railText}>
            {t('web:forma.panel.none')}
          </span>
        )}
      </>
    );
  }
  return (
    <>
      <button
        aria-label={t('web:forma.panel.openRail', { date: rail.dateLabel })}
        className={styles.railThumb}
        onClick={() => {
          onOpenClip(rail.clipId);
        }}
        type="button"
      >
        {rail.posterUrl === null ? null : (
          <img
            alt=""
            className={`${String(photo.photo)} ${String(styles.railThumbImg)}`}
            decoding="async"
            src={rail.posterUrl}
          />
        )}
        <span className={styles.railPlay}>
          <PlayGlyph className={styles.railPlayGlyph} />
        </span>
      </button>
      {rail.unread ? (
        <span className={styles.unreadDot}>
          <span className={hidden.srOnly}>{t('web:forma.panel.new')}</span>
        </span>
      ) : null}
      <span aria-hidden="true" className={styles.railText}>
        {rail.dateLabel}
      </span>
    </>
  );
}
